package com.xorrus.trading.lot.remote.connect

import com.xorrus.trading.lot.relayclient.browser.I18n
import com.xorrus.trading.lot.relayclient.browser.Lang

/** Alle UI-Texte der Connect-SPA; DE/EN-Parität erzwingt der Compiler. */
internal interface Strings {
    val appSubtitle: String

    // ?reason=…-Banner nach Rücksprung aus der Strategie-SPA
    val reasonUnpaired: String
    val reasonDisconnected: String
    val reasonChannelChanged: String
    val reasonUnknownDesktop: String

    // Start & QR-Scan
    val checkingPairing: String

    /** Anleitung über dem Scanner; enthält HTML (rich = true). */
    val scanInstruction: String
    val manualPastePlaceholder: String
    val pairManuallyButton: String
    val backToListButton: String

    // Desktop-Liste
    val desktopListTitle: String
    val addDesktopButton: String
    val desktopListButton: String
    val stateOnline: String
    val stateOffline: String
    val stateRevoked: String
    fun channelLabel(channel: String): String

    // SAS-Bestätigung
    val sasTitle: String
    val sasConfirmHint: String

    // Desktop benennen (nach dem Koppeln bzw. aus der Liste)
    val nameTitle: String
    val nameHint: String
    val namePlaceholder: String
    val saveButton: String
    val skipButton: String
    val renameButton: String
    fun defaultDesktopName(number: Int): String

    // Warteseite & Entkoppeln
    fun waitingTitle(desktopName: String): String
    val waitingDefaultHint: String
    val unpairButton: String
    fun unpairConfirmQuestion(desktopName: String): String
    val unpairConfirmButton: String
    val cancelButton: String

    // Fehler & Poll-Hinweise
    val retryButton: String
    fun startFailed(reason: String?): String
    val notAPairCode: String
    fun pairingFailed(reason: String?): String
    val channelNotReported: String
    fun unknownChannel(channel: String): String
    val desktopOffline: String
    val desktopRevoked: String
    val desktopTooOld: String

    // Kamera-QR-Scanner
    val cameraUnavailableHttps: String
    val cameraAccessDenied: String
}

internal object StringsDe : Strings {
    override val appSubtitle = "Lab of Trades — Fernsteuerung"

    override val reasonUnpaired =
        "Das Gerät wurde entkoppelt. Zum erneuten Verbinden am Desktop ein neues Pairing starten."
    override val reasonDisconnected = "Die Verbindung zum Desktop wurde unterbrochen."
    override val reasonChannelChanged =
        "Die Desktop-App läuft jetzt auf einem anderen Update-Channel — du wirst neu weitergeleitet."
    override val reasonUnknownDesktop = "Dieser Desktop ist auf diesem Gerät nicht (mehr) gekoppelt."

    override val checkingPairing = "Prüfe Kopplung …"
    override val scanInstruction =
        "Öffne am Desktop <b>Sandbox → Pairing → „Neues Gerät koppeln“</b> und scanne den angezeigten QR-Code."
    override val manualPastePlaceholder = "lotpair://… manuell einfügen"
    override val pairManuallyButton = "Manuell koppeln"
    override val backToListButton = "Zurück zur Desktop-Liste"

    override val desktopListTitle = "Welchen Desktop möchtest du steuern?"
    override val addDesktopButton = "Weiteren Desktop koppeln"
    override val desktopListButton = "Andere Desktops"
    override val stateOnline = "online"
    override val stateOffline = "offline"
    override val stateRevoked = "am Desktop entfernt"
    override fun channelLabel(channel: String) = "Channel $channel"

    override val sasTitle = "Sicherheitscode"
    override val sasConfirmHint =
        "Bestätige am Desktop, dass dort derselbe Code angezeigt wird, und wähle die Berechtigungen."

    override val nameTitle = "Wie soll dieser Desktop heißen?"
    override val nameHint =
        "Der Name erscheint nur auf diesem Gerät, zum Beispiel „Büro“ oder „Zuhause“. Du kannst ihn später in der Desktop-Liste ändern."
    override val namePlaceholder = "Name des Desktops"
    override val saveButton = "Speichern"
    override val skipButton = "Überspringen"
    override val renameButton = "Umbenennen"
    override fun defaultDesktopName(number: Int) = "Desktop $number"

    override fun waitingTitle(desktopName: String) = "Warte auf „$desktopName“ …"
    override val waitingDefaultHint = "Sobald die Desktop-App erreichbar ist, geht es automatisch weiter."
    override val unpairButton = "Desktop entkoppeln"
    override fun unpairConfirmQuestion(desktopName: String) =
        "„$desktopName“ wirklich entkoppeln? Danach ist für diesen Desktop ein neues Pairing nötig."
    override val unpairConfirmButton = "Entkoppeln"
    override val cancelButton = "Abbrechen"

    override val retryButton = "Erneut versuchen"
    override fun startFailed(reason: String?) = "Start fehlgeschlagen: $reason"
    override val notAPairCode = "Das ist kein LOT-Kopplungscode (erwartet lotpair://…)."
    override fun pairingFailed(reason: String?) =
        "Kopplung fehlgeschlagen: ${reason ?: "unbekannter Fehler"}. " +
            "Der QR-Code ist nur 90 Sekunden gültig — am Desktop neu starten."
    override val channelNotReported =
        "Die Desktop-App meldet ihren Update-Channel noch nicht — bitte die Desktop-App neu starten, damit sie Updates lädt."
    override fun unknownChannel(channel: String) =
        "Unbekannter Update-Channel „$channel“ — bitte die Desktop-App aktualisieren."
    override val desktopOffline = "Der Desktop ist offline — Lab of Trades am Rechner starten."
    override val desktopRevoked = "Dieses Gerät wurde am Desktop entfernt — hier entkoppeln und neu koppeln."
    override val desktopTooOld =
        "Die Desktop-App ist zu alt für die Fernsteuerung — bitte die Desktop-App neu starten, damit sie Updates lädt."

    override val cameraUnavailableHttps = "Kamera nicht verfügbar — die Seite muss über HTTPS laufen."
    override val cameraAccessDenied = "Kamera-Zugriff verweigert. Alternativ den Code unten manuell einfügen."
}

internal object StringsEn : Strings {
    override val appSubtitle = "Lab of Trades — Remote Control"

    override val reasonUnpaired =
        "This device was unpaired. To reconnect, start a new pairing on the desktop."
    override val reasonDisconnected = "The connection to the desktop was interrupted."
    override val reasonChannelChanged =
        "The desktop app now runs on a different update channel — you will be redirected."
    override val reasonUnknownDesktop = "This desktop is not (or no longer) paired on this device."

    override val checkingPairing = "Checking pairing …"
    override val scanInstruction =
        "On the desktop, open <b>Sandbox → Pairing → “Pair new device”</b> and scan the displayed QR code."
    override val manualPastePlaceholder = "paste lotpair://… manually"
    override val pairManuallyButton = "Pair manually"
    override val backToListButton = "Back to desktop list"

    override val desktopListTitle = "Which desktop do you want to control?"
    override val addDesktopButton = "Pair another desktop"
    override val desktopListButton = "Other desktops"
    override val stateOnline = "online"
    override val stateOffline = "offline"
    override val stateRevoked = "removed on desktop"
    override fun channelLabel(channel: String) = "Channel $channel"

    override val sasTitle = "Security code"
    override val sasConfirmHint =
        "Confirm on the desktop that the same code is shown there, and choose the permissions."

    override val nameTitle = "What should this desktop be called?"
    override val nameHint =
        "The name is only shown on this device, for example “Office” or “Home”. You can change it later in the desktop list."
    override val namePlaceholder = "Desktop name"
    override val saveButton = "Save"
    override val skipButton = "Skip"
    override val renameButton = "Rename"
    override fun defaultDesktopName(number: Int) = "Desktop $number"

    override fun waitingTitle(desktopName: String) = "Waiting for “$desktopName” …"
    override val waitingDefaultHint = "As soon as the desktop app is reachable, you will continue automatically."
    override val unpairButton = "Unpair desktop"
    override fun unpairConfirmQuestion(desktopName: String) =
        "Really unpair “$desktopName”? A new pairing will be required for this desktop."
    override val unpairConfirmButton = "Unpair"
    override val cancelButton = "Cancel"

    override val retryButton = "Try again"
    override fun startFailed(reason: String?) = "Startup failed: $reason"
    override val notAPairCode = "This is not a LOT pairing code (expected lotpair://…)."
    override fun pairingFailed(reason: String?) =
        "Pairing failed: ${reason ?: "unknown error"}. " +
            "The QR code is only valid for 90 seconds — restart it on the desktop."
    override val channelNotReported =
        "The desktop app does not report its update channel yet — please restart the desktop app so it loads updates."
    override fun unknownChannel(channel: String) =
        "Unknown update channel “$channel” — please update the desktop app."
    override val desktopOffline = "The desktop is offline — start Lab of Trades on your computer."
    override val desktopRevoked = "This device was removed on the desktop — unpair here and pair again."
    override val desktopTooOld =
        "The desktop app is too old for remote control — please restart the desktop app so it loads updates."

    override val cameraUnavailableHttps = "Camera not available — the page must be served over HTTPS."
    override val cameraAccessDenied = "Camera access denied. Alternatively, paste the code below manually."
}

/** Aktive Textfassung dieser Seite. */
internal val S: Strings = if (I18n.lang == Lang.DE) StringsDe else StringsEn
